import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, ArrowUp, ArrowDown } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { toCanonical, getDisplayPeriods, type BindingPeriodKey } from '@/utils/bindingPeriods';
import { formatSwedishLongDate } from '@/utils/dateUtils';
import { MORTGAGE_BANKS, canonicalizeMortgageBankName, getMortgageBankSlug } from '@/utils/mortgageBanks';

interface MortgageRate {
  bank_name: string;
  binding_period: string;
  list_rate: number | null;
  average_rate: number | null;
  previous_list_rate: number | null;
  previous_average_rate: number | null;
  scraped_at: string;
  updated_at: string;
}

// Use centralized binding periods configuration
const ALL_BINDING_PERIODS = getDisplayPeriods();

const BANKS = MORTGAGE_BANKS.map((bank) => bank.name);

interface MortgageRatesTableProps {
  maxYears?: number; // Maximum years to display (e.g., 5 for global table, undefined for all)
}

export const MortgageRatesTable = ({ maxYears }: MortgageRatesTableProps = {}) => {
  const { currentLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const [sortConfig, setSortConfig] = useState<{
    column: string | null;
    direction: 'asc' | 'desc';
  }>({ column: null, direction: 'asc' });
  const [activeTab, setActiveTab] = useState<'list' | 'average'>('average');

  // Filter binding periods based on maxYears prop
  const BINDING_PERIODS = maxYears 
    ? ALL_BINDING_PERIODS.filter(period => {
        const yearMatch = period.key.match(/(\d+)_/);
        if (!yearMatch) return true; // Include '3_months' (less than 1 year)
        const years = parseInt(yearMatch[1]);
        return years <= maxYears;
      })
    : ALL_BINDING_PERIODS;

  const { data: rates, isLoading, error } = useQuery({
    queryKey: ['mortgage-rates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mortgage_rates')
        .select('*')
        .order('updated_at', { ascending: false, nullsFirst: false })
        .order('scraped_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) return { rates: [], lastUpdated: null };

      // Normalize all binding periods to canonical keys
      const normalizedData = data.map(rate => ({
        ...rate,
        bank_name: canonicalizeMortgageBankName(rate.bank_name),
        binding_period: toCanonical(rate.binding_period) as string
      }));

      // Get the latest rate for each bank/period combination
      const latestRatesMap = new Map<string, MortgageRate>();
      for (const rate of normalizedData) {
        const key = `${rate.bank_name}-${rate.binding_period}`;
        if (!latestRatesMap.has(key)) {
          latestRatesMap.set(key, rate as MortgageRate);
        }
      }

      // Get all unique rates and find the latest update date
      const allRates = Array.from(latestRatesMap.values());
      const latestDate = allRates.reduce((max, rate) => {
        const rateDate = rate.updated_at || rate.scraped_at;
        return rateDate > max ? rateDate : max;
      }, '');

      return {
        rates: allRates,
        lastUpdated: latestDate
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true, // Always fetch fresh data on mount
    refetchOnWindowFocus: false
  });

  // Formatera tal med svenskt decimalkomma
  const formatRate = (rate: number): string => {
    return rate.toFixed(2).replace('.', ',');
  };

  const getRateDisplay = (
    currentRate: number | null,
    previousRate: number | null
  ) => {
    if (!currentRate) return <span className="text-muted-foreground">–</span>;
    
    const rateText = `${formatRate(currentRate)}%`;
    
    // Visa färgad bakgrund och pil om räntan har ändrats
    if (previousRate !== null && previousRate !== currentRate) {
      const diff = currentRate - previousRate;
      const diffText = diff > 0 ? `+${formatRate(diff)}` : formatRate(diff);
      
      if (diff < 0) {
        // Räntan har sjunkit - grön bakgrund
        return (
          <div className="flex flex-col items-center justify-center rounded-md bg-success/10 px-1.5 py-1 sm:px-2">
            <span className="font-semibold text-foreground">{rateText}</span>
            <span className="flex items-center gap-0.5 text-xs text-success">
              <ArrowDown className="h-3 w-3" />
              <span>{diffText}%</span>
            </span>
          </div>
        );
      } else if (diff > 0) {
        // Räntan har ökat - röd bakgrund
        return (
          <div className="flex flex-col items-center justify-center rounded-md bg-destructive/10 px-1.5 py-1 sm:px-2">
            <span className="font-semibold text-foreground">{rateText}</span>
            <span className="flex items-center gap-0.5 text-xs text-destructive">
              <ArrowUp className="h-3 w-3" />
              <span>{diffText}%</span>
            </span>
          </div>
        );
      }
    }
    
    return <span className="font-semibold">{rateText}</span>;
  };

  const handleSort = (column: string) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const getRateValue = (bankName: string, period: string, type: 'list' | 'average'): number | null => {
    // Normalize the period for consistent comparison
    const normalizedPeriod = toCanonical(period);
    const rate = rates?.rates.find(
      r => r.bank_name === bankName && r.binding_period === normalizedPeriod
    );
    if (!rate) return null;
    return type === 'list' ? rate.list_rate : rate.average_rate;
  };

  const sortBanks = (banks: string[]) => {
    if (!sortConfig.column) {
      return banks;
    }

    if (sortConfig.column === 'bank') {
      // Alfabetisk sortering på banknamn
      return [...banks].sort((a, b) => {
        const comparison = a.localeCompare(b, 'sv-SE');
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return [...banks].sort((a, b) => {
      const rateA = getRateValue(a, sortConfig.column!, activeTab);
      const rateB = getRateValue(b, sortConfig.column!, activeTab);

      if (rateA === null && rateB === null) return 0;
      if (rateA === null) return 1;
      if (rateB === null) return -1;

      const comparison = rateA - rateB;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  };

  const renderBankName = (bankName: string) => {
    const slug = getMortgageBankSlug(bankName);
    
    return (
      <Link 
        to={`/${currentLanguage}/banker/${slug}`}
        className="font-medium text-foreground transition-colors hover:text-primary hover:underline"
      >
        <span>{bankName}</span>
      </Link>
    );
  };

  const getRateForBank = (bankName: string, period: string, type: 'list' | 'average') => {
    const rate = rates?.rates.find(
      r => r.bank_name === bankName && r.binding_period === period
    );
    
    if (!rate) return '–';

    const currentRate = type === 'list' ? rate.list_rate : rate.average_rate;
    const previousRate = type === 'list' ? rate.previous_list_rate : rate.previous_average_rate;

    if (currentRate === null) return '–';

    return getRateDisplay(currentRate, previousRate);
  };

  if (isLoading) {
    return (
      <Card className="max-w-6xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">Aktuella bolåneräntor</CardTitle>
          <CardDescription>Listräntor och snitträntor från Sveriges banker</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-6xl mx-auto">
        <AlertDescription>
          Det gick inte att hämta bolåneräntor. Försök igen senare.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="max-w-6xl mx-auto">
      <CardHeader>
        <CardDescription>
          {rates?.lastUpdated && (
            <span className="text-sm">
              Senast uppdaterad: {formatSwedishLongDate(rates.lastUpdated)}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="average" className="w-full" onValueChange={(v) => setActiveTab(v as 'list' | 'average')}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="average">Snitträntor</TabsTrigger>
            <TabsTrigger value="list">Listräntor</TabsTrigger>
          </TabsList>

          <TabsContent value="average">
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className={`font-bold cursor-pointer hover:bg-muted/50 ${isMobile ? 'min-w-[100px] text-xs' : 'min-w-[140px]'}`}
                      onClick={() => handleSort('bank')}
                    >
                      <div className="flex items-center gap-1">
                        Bank
                        {sortConfig.column === 'bank' && (
                          sortConfig.direction === 'asc' 
                            ? <ArrowUp className="h-3 w-3" />
                            : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </TableHead>
                    {BINDING_PERIODS.map(period => (
                      <TableHead 
                        key={period.key} 
                        className={`text-center font-bold cursor-pointer hover:bg-muted/50 ${isMobile ? 'min-w-[70px] text-xs px-2' : 'min-w-[90px]'}`}
                        onClick={() => handleSort(period.key)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {period.label}
                          {sortConfig.column === period.key && (
                            sortConfig.direction === 'asc' 
                              ? <ArrowUp className="h-3 w-3" />
                              : <ArrowDown className="h-3 w-3" />
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortBanks(BANKS).map(bank => (
                    <TableRow key={bank}>
                      <TableCell className={`font-medium ${isMobile ? 'text-xs py-2' : ''}`}>
                        {renderBankName(bank)}
                      </TableCell>
                      {BINDING_PERIODS.map(period => (
                        <TableCell key={period.key} className={`text-center ${isMobile ? 'text-xs py-2 px-2' : ''}`}>
                          {getRateForBank(bank, period.key, 'average')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="list">
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className={`font-bold cursor-pointer hover:bg-muted/50 ${isMobile ? 'min-w-[100px] text-xs' : 'min-w-[140px]'}`}
                      onClick={() => handleSort('bank')}
                    >
                      <div className="flex items-center gap-1">
                        Bank
                        {sortConfig.column === 'bank' && (
                          sortConfig.direction === 'asc' 
                            ? <ArrowUp className="h-3 w-3" />
                            : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </TableHead>
                    {BINDING_PERIODS.map(period => (
                      <TableHead 
                        key={period.key} 
                        className={`text-center font-bold cursor-pointer hover:bg-muted/50 ${isMobile ? 'min-w-[70px] text-xs px-2' : 'min-w-[90px]'}`}
                        onClick={() => handleSort(period.key)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {period.label}
                          {sortConfig.column === period.key && (
                            sortConfig.direction === 'asc' 
                              ? <ArrowUp className="h-3 w-3" />
                              : <ArrowDown className="h-3 w-3" />
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortBanks(BANKS).map(bank => (
                    <TableRow key={bank}>
                      <TableCell className={`font-medium ${isMobile ? 'text-xs py-2' : ''}`}>
                        {renderBankName(bank)}
                      </TableCell>
                      {BINDING_PERIODS.map(period => (
                        <TableCell key={period.key} className={`text-center ${isMobile ? 'text-xs py-2 px-2' : ''}`}>
                          {getRateForBank(bank, period.key, 'list')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        <Alert className="max-w-5xl mx-auto mt-6">
          <Info className="h-5 w-5" />
          <AlertDescription className="ml-2">
            <strong>Obs:</strong> Listräntan är den officiella räntan som banken marknadsför,
            medan snitträntan visar den genomsnittliga räntan som befintliga kunder har fått.
            Snitträntan är ofta lägre och ger en mer realistisk bild av vad du kan förvänta dig.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};
